import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import emailjs from "@emailjs/browser";
import { useNavigate } from "react-router-dom";
import { REGISTER_URL, COMPANY_URL, STAFF_URL } from "../../apiConfig";
import { Lock, Loader2, CheckCircle2, Check, ClipboardList, ArrowDown, AlertTriangle, Zap, FileSpreadsheet, IdCard } from "lucide-react";
import SearchableDropdown from "../SearchableDropdown";

// Initialize EmailJS
emailjs.init("ns8iBr8YNGxt5OpPN");

// Helper function to load logo as base64
const loadLogoAsBase64 = async () => {
  try {
    const response = await fetch('/logo.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

export default function ExistingCompanyForm({ courseData, lookups }) {
  const navigate = useNavigate(); // Add this hook
  const blankTrainee = {
    studentName: "",
    idType: "NRIC",
    idNumber: "",
    dob: "",
    traineeEmail: "",
    gender: "",
    nationality: "",
    race: "",
    traineeContact: "",
    workPermit: null,
    wsqCert: null,
    educationCert: null,
  };

  const [formData, setFormData] = useState({
    uen: "",
    clientEmail: "",
    contactPerson: "",
    contactNumber: "",
    trainees: [{ ...blankTrainee }],
    courseName: courseData?.title || "Certificate of Competency in Welding",
    courseAmount: courseData?.price ? (courseData.price * 1.09).toFixed(2) : "3270.00",
    howKnow: "",
    knowStaff: "",
    staffName: "",
    staffEmail: "",
    agree: false,
  });

  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 🟢 CUSTOM ALERT STATE & OVERRIDE
  const [customAlert, setCustomAlert] = useState({ isOpen: false, message: "" });
  const alert = (message) => {
    setCustomAlert({ isOpen: true, message: String(message) });
  };

  // 🟢 UEN AUTO-LOOKUP STATE
  const [uenLookupState, setUenLookupState] = useState('idle'); // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  const [uenCompanyName, setUenCompanyName] = useState('');
  const uenDebounceTimer = useRef(null);

  // 🟢 SALES STAFF AUTO-LOOKUP STATE
  const [staffLookupState, setStaffLookupState] = useState('idle'); // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  const [staffOfficialName, setStaffOfficialName] = useState('');
  const staffDebounceTimer = useRef(null);

  const RECAPTCHA_SITE_KEY = "6LdhwBcsAAAAAKDrnrh8YCtsmNmn7B2ZOvA7GroR";

  // GST CALCULATION
  useEffect(() => {
    if (courseData?.price) {
      const basePrice = courseData.price || 0;
      const totalAmount = basePrice * formData.trainees.length;
      const totalGst = totalAmount * 0.09;
      const grandTotal = totalAmount + totalGst;

      setFormData(prev => ({
        ...prev,
        courseName: courseData.title || "",
        courseAmount: grandTotal.toFixed(2)
      }));
    }
  }, [courseData, formData.trainees.length]);

  // Initialize reCAPTCHA v3
  useEffect(() => {
    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.addEventListener('load', () => {
        console.log('reCAPTCHA v3 loaded');
      });
      document.body.appendChild(script);
    };
    loadRecaptcha();
  }, []);

  const executeRecaptcha = async () => {
    try {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not loaded');
        return null;
      }
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
      setRecaptchaToken(token);
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  // 🟢 UEN AUTO-LOOKUP EFFECT — debounced, triggers after UEN has 6+ chars
  useEffect(() => {
    const uen = formData.uen.trim();
    if (uen.length < 6) {
      setUenLookupState('idle');
      setUenCompanyName('');
      return;
    }

    if (uenDebounceTimer.current) clearTimeout(uenDebounceTimer.current);
    setUenLookupState('loading');

    uenDebounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${COMPANY_URL}?uen=${encodeURIComponent(uen.toUpperCase())}`);
        const data = await res.json();

        if (data.success && data.found && data.company) {
          const c = data.company;
          setUenLookupState('found');
          setUenCompanyName(c.company_name || '');
          // Auto-fill company fields
          setFormData(prev => ({
            ...prev,
            clientEmail: c.primary_email || prev.clientEmail,
            contactPerson: c.primary_contact || prev.contactPerson,
            contactNumber: c.primary_phone || prev.contactNumber,
          }));
        } else {
          setUenLookupState('not_found');
          setUenCompanyName('');
        }
      } catch (err) {
        console.error('UEN lookup error:', err);
        setUenLookupState('error');
        setUenCompanyName('');
      }
    }, 700);

    return () => clearTimeout(uenDebounceTimer.current);
  }, [formData.uen]);

  // 🟢 SALES STAFF AUTO-LOOKUP EFFECT — debounced, triggers after staffName has 3+ chars
  useEffect(() => {
    const name = formData.staffName.trim();
    if (name.length < 3) {
      setStaffLookupState('idle');
      setStaffOfficialName('');
      return;
    }

    if (staffDebounceTimer.current) clearTimeout(staffDebounceTimer.current);
    setStaffLookupState('loading');

    staffDebounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${STAFF_URL}?name=${encodeURIComponent(name)}`);
        const data = await res.json();

        if (data.success && data.found && data.staff) {
          const s = data.staff;
          setStaffLookupState('found');
          setStaffOfficialName(s.staff_name || '');
          // Auto-fill staff fields
          setFormData(prev => ({
            ...prev,
            staffName: s.staff_name || prev.staffName,
            staffEmail: s.email || prev.staffEmail,
          }));
        } else {
          setStaffLookupState('not_found');
          setStaffOfficialName('');
        }
      } catch (err) {
        console.error('Staff lookup error:', err);
        setStaffLookupState('error');
        setStaffOfficialName('');
      }
    }, 700);

    return () => clearTimeout(staffDebounceTimer.current);
  }, [formData.staffName]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "knowStaff" && value === "No") {
      setStaffLookupState('idle');
      setStaffOfficialName('');
      setFormData(prev => ({
        ...prev,
        knowStaff: "No",
        staffName: "",
        staffEmail: "",
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleTraineeChange = (index, e) => {
    const { name, value } = e.target;
    const updatedTrainees = [...formData.trainees];
    updatedTrainees[index][name] = value;
    setFormData({ ...formData, trainees: updatedTrainees });
  };

  const handleTraineeFileChange = (index, name, file) => {
    const updatedTrainees = [...formData.trainees];
    updatedTrainees[index][name] = file;
    setFormData({ ...formData, trainees: updatedTrainees });
  };

  const addTrainee = () => {
    // Dynamic capacity calculation
    const maxPax = courseData?.schedule?.max_trainee ? Number(courseData.schedule.max_trainee) : 20;
    const currentPax = courseData?.schedule?.current_trainee ? Number(courseData.schedule.current_trainee) : 0;
    const availableSlots = Math.max(0, maxPax - currentPax);

    if (formData.trainees.length >= availableSlots) {
      alert(`Maximum capacity of ${availableSlots} trainees reached for this schedule.\nPlease book another schedule for additional trainees.`);
      return;
    }
    setFormData({
      ...formData,
      trainees: [...formData.trainees, { ...blankTrainee }],
    });
  };

  const removeTrainee = (index) => {
    const updatedTrainees = formData.trainees.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      trainees: updatedTrainees.length ? updatedTrainees : [{ ...blankTrainee }],
    });
  };

  const smartColumnDetect = (row) => {
    const findValue = (possibleKeys) => {
      for (const key of Object.keys(row)) {
        const normalizedKey = key.toLowerCase().trim().replace(/\s+/g, ' ');
        for (const possibleKey of possibleKeys) {
          if (normalizedKey.includes(possibleKey.toLowerCase())) {
            const val = row[key];
            return val !== null && val !== undefined ? String(val).trim() : "";
          }
        }
      }
      return "";
    };

    return {
      studentName: findValue(['name', 'student name', 'full name', 'trainee name']),
      idNumber: findValue(['nric', 'ic', 'passport', 'id', 'identification', 'id number']),
      dob: findValue(['dob', 'date of birth', 'birth date', 'birthday']),
      traineeEmail: findValue(['email', 'e-mail', 'email address']),
      gender: findValue(['gender', 'sex']),
      nationality: findValue(['nationality', 'country']),
      race: findValue(['race', 'ethnicity']),
      traineeContact: findValue(['contact', 'phone', 'mobile', 'contact number', 'phone number']),
      workPermit: findValue(['work permit upload', 'nric upload', 'nric base64', 'wp base64', 'nric file']) || null,
      wsqCert: findValue(['wsq upload', 'wsq certificate upload', 'wsq base64', 'wsq file']) || null,
      educationCert: findValue(['education upload', 'highest education upload', 'education certificate upload', 'education base64', 'edu base64', 'education file']) || null,
    };
  };

  const handleBulkExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkLoading(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

      if (!rows || !rows.length) {
        alert("Excel file appears empty or has no data rows");
        setBulkLoading(false);
        return;
      }

      const mapped = rows.map((row) => smartColumnDetect(row));
      const filtered = mapped.filter((stu) => {
        return stu.studentName && stu.studentName.length > 0;
      });

      if (filtered.length === 0) {
        alert("No valid student records found in Excel file");
        setBulkLoading(false);
        return;
      }
      // Append to existing trainees, filtering out completely blank manual rows
      const existingTrainees = formData.trainees.filter(
        t => t.studentName || t.idNumber || t.traineeEmail || t.traineeContact
      );
      
      // Dynamic capacity calculation
      const maxPax = courseData?.schedule?.max_trainee ? Number(courseData.schedule.max_trainee) : 20;
      const currentPax = courseData?.schedule?.current_trainee ? Number(courseData.schedule.current_trainee) : 0;
      const availableSlots = Math.max(0, maxPax - currentPax);

      const currentCount = existingTrainees.length;
      const incomingCount = filtered.length;

      if (currentCount + incomingCount > availableSlots) {
        const allowedNew = Math.max(0, availableSlots - currentCount);
        if (allowedNew === 0) {
           alert(`Maximum capacity reached! There are only ${availableSlots} slots left in this schedule.\nPlease book another schedule for the remaining trainees.`);
        } else {
           alert(`⚠️ Only ${availableSlots} slots available in this batch!\n\nYou already have ${currentCount} enrolled. We will only import the first ${allowedNew} trainees from this Excel file.\n\nPlease register the remaining trainees in a future or next batch.`);
        }
        
        const fitFiltered = filtered.slice(0, allowedNew);
        setFormData({ ...formData, trainees: [...existingTrainees, ...fitFiltered] });
      } else {
        setFormData({ ...formData, trainees: [...existingTrainees, ...filtered] });
        alert(`Successfully imported ${filtered.length} students from Excel`);
      }

    } catch (err) {
      console.error("Bulk excel error:", err);
      alert("Failed to read Excel file. Please ensure it's a valid .xlsx/.xls file with student data.");
    } finally {
      setBulkLoading(false);
      e.target.value = null;
    }
  };

  // 🔥 GENERATE PDF FUNCTION - WITH ACTUAL LOGO IMAGE
  const generatePDF = async () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString('en-GB');
    let yPos = 15;

    // Logo and Header - Enhanced Design with Visual Logo
    // Green background header (taller for logo)
    doc.setFillColor(11, 153, 73);
    doc.rect(0, 0, 210, 40, 'F');

    // Try to add actual logo image
    try {
      const logoBase64 = await loadLogoAsBase64();
      if (logoBase64) {
        // Add logo image (20x20mm at position 15,10)
        doc.addImage(logoBase64, 'PNG', 15, 10, 20, 20);
      }
    } catch (error) {
      console.error('Could not add logo to PDF:', error);
    }

    // Company name next to logo
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(26);
    doc.setFont("helvetica", "bold");
    doc.text("GREENMARK", 45, 18);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Training Institute", 45, 25);

    // Form title
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("COURSE REGISTRATION FORM", 105, 35, { align: "center" });

    yPos = 50;
    doc.setTextColor(0, 0, 0);

    // Course Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("COURSE INFORMATION:", 14, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Course Name: ${formData.courseName}`, 14, yPos);
    yPos += 6;
    doc.text(`Level: ${courseData?.selectedLevel?.toUpperCase() || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Total Amount (incl. GST): $${formData.courseAmount}`, 14, yPos);
    yPos += 6;
    doc.text(`Number of Trainees: ${formData.trainees.length}`, 14, yPos);
    yPos += 11;

    // Company Details
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("COMPANY DETAILS:", 14, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`UEN: ${formData.uen}`, 14, yPos);
    yPos += 6;
    doc.text(`Company Email: ${formData.clientEmail}`, 14, yPos);
    yPos += 6;
    doc.text(`Contact Person: ${formData.contactPerson}`, 14, yPos);
    yPos += 6;
    doc.text(`Contact Number: ${formData.contactNumber}`, 14, yPos);
    yPos += 11;

    // Trainees Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("TRAINEES INFORMATION:", 14, yPos);
    yPos += 7;

    // Table Header
    doc.setFillColor(11, 153, 73);
    doc.rect(14, yPos, 182, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Name", 16, yPos + 5);
    doc.text("ID Number", 50, yPos + 5);
    doc.text("DOB", 65, yPos + 5);
    doc.text("Email", 80, yPos + 5);
    doc.text("Gender", 120, yPos + 5);
    doc.text("Nationality", 140, yPos + 5);
    doc.text("Race", 170, yPos + 5);
    yPos += 8;

    // Table Body
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    formData.trainees.forEach((trainee, index) => {
      // Check if we need a new page
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }

      // Alternate row colors
      if (index % 2 === 0) {
        doc.setFillColor(240, 240, 240);
        doc.rect(14, yPos, 182, 8, 'F');
      }

      doc.text(trainee.studentName.substring(0, 18) || 'N/A', 16, yPos + 5);
      doc.text(trainee.idNumber.substring(0, 15) || 'N/A', 50, yPos + 5);
      doc.text(trainee.dob.substring(0, 15) || 'N/A', 65, yPos + 5);
      doc.text(trainee.traineeEmail.substring(0, 20) || 'N/A', 80, yPos + 5);
      doc.text(trainee.gender || 'N/A', 120, yPos + 5);
      doc.text(trainee.nationality || 'N/A', 140, yPos + 5);
      doc.text(trainee.race || 'N/A', 170, yPos + 5);
      yPos += 8;
    });

    yPos += 10;

    // Additional Information
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("ADDITIONAL INFORMATION:", 14, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`How did you know about us: ${formData.howKnow || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Know our staff: ${formData.knowStaff || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Staff Name: ${formData.staffName || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Staff Email: ${formData.staffEmail || 'N/A'}`, 14, yPos);
    yPos += 15;

    // Footer with Contact Information
    if (yPos > 260) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFillColor(245, 245, 245);
    doc.rect(0, yPos, 210, 30, 'F');
    doc.setFontSize(10);
    doc.setTextColor(11, 153, 73);
    doc.setFont("helvetica", "bold");
    doc.text("GREENMARK Training Institute", 105, yPos + 6, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8);
    doc.text("Email: pcm@greenmark.com.sg | Phone: +65 8014 7799", 105, yPos + 12, { align: "center" });
    doc.text("Website: www.greenmark.com.sg", 105, yPos + 17, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated on: ${currentDate}`, 105, yPos + 24, { align: "center" });

    return doc;
  };

  // 🔥 SEND EMAIL WITH COMPLETE HTML BODY - ENHANCED WITH LOGO AND CONTACT INFO
  const sendEmailWithPDF = async () => {
    try {
      // Build trainee table rows
      const traineesHTML = formData.trainees
        .map(
          (t, i) => `
        <tr>
          <td style="padding:8px; border:1px solid #ddd;">${i + 1}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.studentName}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.dob}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.idNumber}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.traineeEmail || "N/A"}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.gender || "N/A"}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.nationality || "N/A"}</td>
          <td style="padding:8px; border:1px solid #ddd;">${t.race || "N/A"}</td>
        </tr>
      `
        )
        .join("");

      // EMAIL-CLIENT-SAFE TEMPLATE (works on mobile!) - ENHANCED VERSION
      const fullEmailHTML = `
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4; padding:20px; font-family:Arial, sans-serif;">
      <tr>
        <td>
          <table width="100%" cellpadding="0" cellspacing="0" 
            style="max-width:650px; margin:auto; background:#ffffff; border-radius:8px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.1);">

            <!-- Logo Header -->
            <tr>
              <td style="background:#0b9949; padding:30px 20px; text-align:center;">
                <h1 style="color:#ffffff; margin:0; font-size:32px; font-family:Arial, sans-serif; letter-spacing:1px;">GREENMARK</h1>
                <p style="color:#ffffff; margin:8px 0 0 0; font-size:14px; font-family:Arial, sans-serif;">Training Institute</p>
              </td>
            </tr>

         
            <!-- Summary -->
            <tr>
              <td style="padding:20px; font-family:Arial; font-size:14px; color:#333;">
                <h3 style="color:#0b9949; margin:0 0 15px 0; border-bottom:2px solid #0b9949; padding-bottom:8px;">📋 Existing Company Registration Summary</h3>

                <table width="100%" cellpadding="8" cellspacing="0" style="background:#f9f9f9; border-radius:6px;">
                  <tr><td style="padding:8px;"><strong>UEN:</strong></td><td style="padding:8px;">${formData.uen}</td></tr>
                  <tr><td style="padding:8px; background:#fff;"><strong>Company Email:</strong></td><td style="padding:8px; background:#fff;">${formData.clientEmail}</td></tr>
                  <tr><td style="padding:8px;"><strong>Contact Person:</strong></td><td style="padding:8px;">${formData.contactPerson}</td></tr>
                  <tr><td style="padding:8px; background:#fff;"><strong>Contact Number:</strong></td><td style="padding:8px; background:#fff;">${formData.contactNumber || "N/A"}</td></tr>
                  <tr><td style="padding:8px;"><strong>Course Name:</strong></td><td style="padding:8px;">${formData.courseName}</td></tr>
                  <tr><td style="padding:8px; background:#fff;"><strong>Course Level:</strong></td><td style="padding:8px; background:#fff;">${courseData?.selectedLevel?.toUpperCase() || 'N/A'}</td></tr>
                  <tr><td style="padding:8px;"><strong>Total Trainees:</strong></td><td style="padding:8px;">${formData.trainees.length}</td></tr>
                  <tr><td style="padding:8px; background:#fff;"><strong>Total Amount (incl. GST):</strong></td><td style="padding:8px; background:#fff;"><span style="color:#0b9949; font-size:20px; font-weight:bold;">$${formData.courseAmount}</span></td></tr>
                </table>
              </td>
            </tr>

            <!-- Trainee Table -->
            <tr>
              <td style="padding:20px; font-family:Arial;">
                <h3 style="color:#0b9949; margin:0 0 15px 0; border-bottom:2px solid #0b9949; padding-bottom:8px;">👥 Trainees Information</h3>

                <div style="overflow-x:auto;">
                  <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse; min-width:600px;">
                    <thead>
                      <tr style="background:#0b9949; color:white; font-size:12px;">
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">#</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">Name</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">DOB</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">ID</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">Email</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">Gender</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">Nationality</th>
                        <th style="padding:10px; border:1px solid #ddd; text-align:left;">Race</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${traineesHTML}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>

            <!-- Additional Info -->
            <tr>
              <td style="padding:20px; font-family:Arial; font-size:14px;">
                <h3 style="color:#0b9949; margin:0 0 15px 0; border-bottom:2px solid #0b9949; padding-bottom:8px;">📌 Additional Information</h3>

                <p style="margin:8px 0;"><strong>How did you know us:</strong> ${formData.howKnow || "N/A"}</p>
                <p style="margin:8px 0;"><strong>Know our staff:</strong> ${formData.knowStaff || "N/A"}</p>
                <p style="margin:8px 0;"><strong>Staff Name:</strong> ${formData.staffName || "N/A"}</p>
                <p style="margin:8px 0;"><strong>Staff Email:</strong> ${formData.staffEmail || "N/A"}</p>
              </td>
            </tr>

            <!-- Footer with Contact Info -->
            <tr>
              <td style="background:#f9f9f9; padding:25px 20px; text-align:center; font-size:13px; color:#666; font-family:Arial; border-top:3px solid #0b9949;">
                <p style="margin:0 0 10px 0; color:#0b9949; font-weight:bold; font-size:16px;">GREENMARK Training Institute</p>
                <p style="margin:5px 0; color:#555;">📧 Email: <a href="mailto:pcm@greenmark.com.sg" style="color:#0b9949; text-decoration:none;">info@greenmark.com.sg</a></p>
                <p style="margin:5px 0; color:#555;">📞 Phone: <a href="tel:+658014 7799" style="color:#0b9949; text-decoration:none;">+65 1234 5678</a></p>
                <p style="margin:5px 0; color:#555;">🌐 Website: <a href="http://www.greenmark.com.sg" style="color:#0b9949; text-decoration:none;">www.greenmark.com.sg</a></p>
                <p style="margin:15px 0 5px 0; font-size:11px; color:#999;">Generated on: <strong>${new Date().toLocaleDateString("en-GB")}</strong></p>
                <p style="margin:5px 0; font-size:11px; color:#999;">We will contact you soon with further details.</p>
              </td>
            </tr>

          </table>
        </td>
      </tr>
    </table>
    `;

      // Send Email
      const res = await emailjs.send(
        "service_7pytgxf",
        "template_lbjy8n3",
        {
          message_html: fullEmailHTML,
          to_email: "pcm@greenmark.com.sg",
          reply_to: formData.clientEmail || "noreply@greenmark.com.sg",
        }
      );

      console.log("Email sent:", res);
      return true;

    } catch (err) {
      console.error("Email failed:", err);
      throw err;
    }
  };


  // 🔥 SUCCESS MODAL COMPONENT (Continuous Corporate Partner Dashboard)
  const SuccessModal = ({ onClose }) => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 20, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 overflow-hidden text-center flex flex-col p-8"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={36} className="text-green-600 stroke-[3]" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">Registration Submitted!</h3>
          <p className="text-gray-600 mb-8">
            Thank you for registering <strong>{formData.trainees.length}</strong> trainees under <strong>{formData.companyName}</strong>. 
            We have received your details and will review and respond within a few working days.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-[#0b9949] hover:bg-[#087c3a] text-white py-3 rounded-xl font-bold text-sm tracking-wide shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Go to Corporate Home
          </button>
        </motion.div>
      </motion.div>
    );
  };

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // 🔥 HANDLE FORM SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.uen || !formData.clientEmail || !formData.contactPerson) {
      alert("Please fill all company details!");
      return;
    }

    if (formData.trainees.some(t => !t.studentName || !t.idNumber)) {
      alert("Please fill all trainee details (Name & ID Number are required)!");
      return;
    }

    // 🟢 DUPLICATE NRIC VALIDATION
    const nrics = formData.trainees.map(t => (t.idNumber || "").toLowerCase().trim()).filter(Boolean);
    const uniqueNrics = new Set(nrics);
    if (nrics.length !== uniqueNrics.size) {
      alert("⚠️ Duplicate Identification Numbers found! You have entered the same NRIC/FIN for multiple trainees. Please ensure each trainee has a unique ID.");
      return;
    }

    // 🟢 DUPLICATE EMAIL VALIDATION
    const emails = formData.trainees.map(t => (t.traineeEmail || "").toLowerCase().trim()).filter(Boolean);
    const uniqueEmails = new Set(emails);
    if (emails.length !== uniqueEmails.size) {
      alert("⚠️ Duplicate Emails found! Multiple trainees cannot share the exact same email address. Please ensure each trainee has a unique email.");
      return;
    }

    // 🟢 DUPLICATE CONTACT VALIDATION
    const contacts = formData.trainees.map(t => (t.traineeContact || "").trim()).filter(Boolean);
    const uniqueContacts = new Set(contacts);
    if (contacts.length !== uniqueContacts.size) {
      alert("⚠️ Duplicate Contact Numbers found! Multiple trainees cannot share the exact same phone number. Please ensure each trainee has a unique contact number.");
      return;
    }

    const missingRequired = formData.trainees.findIndex(t => !t.dob || !t.gender || !t.nationality || !t.race);
    if (missingRequired >= 0) {
      alert(`Trainee ${missingRequired + 1}: Please fill Date of Birth, Gender, Nationality and Race — these are required by the registration system.`);
      return;
    }

    if (!formData.agree) {
      alert("Please accept the terms and conditions!");
      return;
    }

    setIsSubmitting(true);

    try {
      // Execute reCAPTCHA
      const token = await executeRecaptcha();
      if (!token) {
        console.warn("reCAPTCHA token not generated, continuing anyway...");
      }

      console.log("🔄 Generating PDF...");
      // Generate PDF (now async)
      const pdfDoc = await generatePDF();

      // console.log("💾 Downloading PDF locally...");
      // Download PDF locally
      // pdfDoc.save(`Course_Registration_${formData.uen}_${Date.now()}.pdf`);

      // Convert files to base64 helper
      const toBase64 = (file) => {
        if (!file) return Promise.resolve(null);
        if (typeof file === 'string') {
          if (file.startsWith('data:')) {
            return Promise.resolve(file);
          }
          if (file.trim().length > 0) {
            return Promise.resolve(`data:application/pdf;base64,${file.trim()}`);
          }
          return Promise.resolve(null);
        }
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      const mappedTrainees = await Promise.all(formData.trainees.map(async (t) => {
        const nricBase64 = await toBase64(t.workPermit);
        const wsqBase64 = await toBase64(t.wsqCert);
        const eduBase64 = await toBase64(t.educationCert);
        return {
          studentName: t.studentName,
          nric: t.idNumber,
          idType: t.idType || "NRIC",
          cardType: t.idType || "NRIC",
          dob: t.dob || null,
          contact: t.traineeContact,
          email: t.traineeEmail,
          gender: t.gender || "",
          nationality: t.nationality || "",
          race: t.race || "",
          nricUpload: nricBase64,
          wsqCertUpload: wsqBase64,
          educationCert: eduBase64
        };
      }));

      // 🟢 Register in database via local PHP proxy
      const registerPayload = {
        sponsorType: "company",
        uen: formData.uen,
        companyName: "Company UEN " + formData.uen, // fallback
        clientEmail: formData.clientEmail,
        contactPerson: formData.contactPerson,
        contactNumber: formData.contactNumber,
        schedule_id: courseData?.schedule?.schedule_id || null,
        course_id: courseData?.schedule?.course_id || null,
        location_id: courseData?.schedule?.location_id || 1,
        language: courseData?.schedule?.course_language || "English",
        course_type: "Public Class",
        discount: 0,
        promo_code: "",
        staffName: formData.staffName || "",
        staffEmail: formData.staffEmail || "",
        trainees: mappedTrainees
      };

      console.log("💾 Registering company sponsor and trainees in database via PHP proxy...", registerPayload);
      const regResponse = await fetch(REGISTER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(registerPayload)
      });

      if (!regResponse.ok) {
        throw new Error(`Failed to save registration to database: ${regResponse.statusText}`);
      }

      const regResult = await regResponse.json();
      console.log("✅ Database registration response:", regResult);
      if (!regResult.success) {
        throw new Error(regResult.error || "Failed to persist trainee registration in database.");
      }

      console.log("📧 Sending email with PDF...");
      // Send email with PDF
      await sendEmailWithPDF(pdfDoc);

      // Show success modal instead of alert
      setShowSuccessModal(true);

    } catch (error) {
      console.error("❌ Submission error:", error);
      alert(`Error: ${error.message || 'Failed to send email. Please try again or contact support.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔥 HANDLE MODAL CLOSE AND REDIRECT
  const handleModalClose = () => {
    window.location.href = "/";
  };

  const renderCourseSummary = () => {
    if (!courseData?.title && !formData.courseName) return null;

    const basePrice = courseData?.price || parseFloat(formData.courseAmount) / 1.09;
    const totalAmount = basePrice * formData.trainees.length;
    const totalGst = totalAmount * 0.09;
    const grandTotal = totalAmount + totalGst;

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2"><ClipboardList size={20} /> Course Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div><span className="font-medium">Course:</span> {formData.courseName}</div>
          <div><span className="font-medium">Base Price per trainee:</span> ${basePrice.toFixed(2)}</div>
          <div><span className="font-medium">Number of Trainees:</span> {formData.trainees.length}</div>
          <div><span className="font-medium">Total Amount:</span> ${totalAmount.toFixed(2)}</div>
          <div><span className="font-medium">GST (9%):</span> ${totalGst.toFixed(2)}</div>
          <div><span className="font-medium text-green-700 font-bold">Grand Total:</span> ${grandTotal.toFixed(2)}</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {customAlert.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-gray-100 overflow-hidden text-center flex flex-col p-6"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${customAlert.message.includes("Error") || customAlert.message.includes("Failed") || customAlert.message.includes("⚠️") || customAlert.message.includes("❌") ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                {customAlert.message.includes("Successfully") ? <CheckCircle2 size={32} /> : <AlertTriangle size={32} />}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Notification</h3>
              <p className="text-gray-600 mb-6 text-sm whitespace-pre-wrap">{customAlert.message}</p>
              <button
                onClick={() => setCustomAlert({ ...customAlert, isOpen: false })}
                className="w-full bg-gray-900 hover:bg-black text-white py-2.5 rounded-xl font-bold text-sm shadow-md transition-all active:scale-95"
              >
                Acknowledge
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto bg-white p-8 shadow-lg rounded-md mt-10 mb-20">
        <div className="space-y-8">

          {/* Course Summary */}
          {renderCourseSummary()}

          {/* Client Company Details */}
          <section>
            <div className="flex items-center gap-3 mb-5">
              <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Client Company Details</h2>
              <span className="text-xs text-slate-400 font-medium bg-slate-100 px-2.5 py-1 rounded-full">Existing Partner</span>
            </div>

            {/* UEN Field with Live Lookup */}
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Company UEN *</label>
              <div className="relative">
                <input
                  type="text"
                  name="uen"
                  placeholder="e.g. 200312345K"
                  value={formData.uen}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide pr-12 transition-all duration-300 outline-none
                    ${uenLookupState === 'found' ? 'border-emerald-400 bg-emerald-50/30 focus:border-emerald-500' : 
                      uenLookupState === 'not_found' ? 'border-amber-300 bg-amber-50/20 focus:border-amber-400' : 
                      uenLookupState === 'error' ? 'border-red-300 bg-red-50/20' :
                      'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                />
                {/* Status Icon */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-base">
                  {uenLookupState === 'loading' && (
                    <svg className="animate-spin w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                  )}
                  {uenLookupState === 'found' && <span className="text-emerald-500 font-bold text-lg"><Check /></span>}
                  {uenLookupState === 'not_found' && <span className="text-amber-400 font-bold text-lg">?</span>}
                  {uenLookupState === 'error' && <span className="text-red-400 font-bold text-lg">!</span>}
                </div>
              </div>

              {/* Live status message */}
              <AnimatePresence mode="wait">
                {uenLookupState === 'found' && (
                  <motion.div
                    key="found"
                    initial={{ opacity: 0, y: -6, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -6, height: 0 }}
                    className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                      <Check className="text-white" size={14} strokeWidth={4} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-extrabold text-emerald-800 uppercase tracking-wide">Existing Partner Found</p>
                      <p className="text-sm font-bold text-emerald-700 mt-0.5">{uenCompanyName}</p>
                      <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">Contact details auto-filled from our records <ArrowDown size={12} /></p>
                    </div>
                  </motion.div>
                )}
                {uenLookupState === 'not_found' && (
                  <motion.div
                    key="not_found"
                    initial={{ opacity: 0, y: -6, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -6, height: 0 }}
                    className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2.5"
                  >
                    <AlertTriangle className="text-amber-500" size={20} />
                    <div>
                      <p className="text-xs font-bold text-amber-800">UEN not in our database</p>
                      <p className="text-[10px] text-amber-600">Please fill your details manually below. You may still submit.</p>
                    </div>
                  </motion.div>
                )}
                {uenLookupState === 'error' && (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, y: -6, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: -6, height: 0 }}
                    className="mt-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 flex items-center gap-2.5"
                  >
                    <Zap className="text-red-400" size={16} />
                    <p className="text-xs font-bold text-red-700">Server lookup failed — please fill details manually</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Remaining company fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="clientEmail"
                  placeholder="Company email address"
                  value={formData.clientEmail}
                  onChange={handleChange}
                  required
                  className="border-2 border-slate-200 rounded-xl px-4 py-3 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Contact Person *</label>
                <input
                  type="text"
                  name="contactPerson"
                  placeholder="Name of primary contact"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                  className="border-2 border-slate-200 rounded-xl px-4 py-3 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Contact Number</label>
                <input
                  type="tel"
                  inputMode="tel"
                  name="contactNumber"
                  placeholder="e.g. +65 9123 4567"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  onKeyPress={(e) => {
                    if (!/[0-9+\-()\s]/.test(e.key)) e.preventDefault();
                  }}
                  className="border-2 border-slate-200 rounded-xl px-4 py-3 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                />
              </div>
            </div>
          </section>

          {/* Trainee Details */}
          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold border-b-2 border-gray-200 pb-2">
                Trainee Details ({formData.trainees.length} student{formData.trainees.length !== 1 ? 's' : ''})
              </h2>

              <div className="text-sm">
                <label className="inline-flex items-center bg-[#0b9949] text-white px-4 py-2 rounded cursor-pointer hover:bg-[#087c3a] transition-all shadow-md">
                  <FileSpreadsheet size={18} className="mr-2" />
                  <input
                    type="file"
                    accept=".xls,.xlsx"
                    className="hidden"
                    onChange={handleBulkExcelUpload}
                  />
                  Import from Excel
                </label>
                {bulkLoading && (
                  <span className="ml-3 text-sm text-gray-600 inline-flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Processing Excel...
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-5 max-h-[720px] overflow-y-auto pr-1">
              {formData.trainees.map((trainee, index) => (
                <div
                  key={index}
                  className="border-2 border-slate-100 hover:border-emerald-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="flex justify-between items-center px-5 py-3.5 bg-gradient-to-r from-slate-50 to-emerald-50/30 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-[#0b9949] text-white text-xs font-black flex items-center justify-center shadow-sm">
                        {index + 1}
                      </div>
                      <span className="text-sm font-bold text-slate-700">Trainee {index + 1}</span>
                      <span className="text-[10px] text-slate-400 font-semibold bg-slate-100 px-2 py-0.5 rounded-full">
                        {trainee.studentName || 'New Entry'}
                      </span>
                    </div>
                    {formData.trainees.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTrainee(index)}
                        className="text-xs text-red-500 hover:text-red-700 font-bold hover:bg-red-50 px-3 py-1 rounded-lg transition-all"
                      >
                        ✕ Remove
                      </button>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-5">

                    {/* Row 1: Name + ID Type + ID Number */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div className="sm:col-span-1">
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Full Name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="studentName"
                          placeholder="As per ID card"
                          value={trainee.studentName}
                          onChange={(e) => handleTraineeChange(index, e)}
                          required
                          className="border-2 border-slate-200 rounded-xl px-3 py-2.5 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          ID Type <span className="text-red-400">*</span>
                        </label>
                        <select
                          name="idType"
                          value={trainee.idType}
                          onChange={(e) => handleTraineeChange(index, e)}
                          required
                          className="border-2 border-slate-200 rounded-xl px-3 py-2.5 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors bg-white"
                        >
                          <option value="NRIC">NRIC</option>
                          <option value="FIN">FIN</option>
                          <option value="Passport">Passport</option>
                          <option value="Work Permit">Work Permit</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          ID Number <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          name="idNumber"
                          placeholder={trainee.idType === 'NRIC' ? 'e.g. S1234567A' : trainee.idType === 'FIN' ? 'e.g. G1234567X' : 'Passport / Permit No.'}
                          value={trainee.idNumber}
                          onChange={(e) => handleTraineeChange(index, e)}
                          required
                          className="border-2 border-slate-200 rounded-xl px-3 py-2.5 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors font-mono tracking-wide"
                        />
                      </div>
                    </div>

                    {/* Row 2: DOB + Gender */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Date of Birth <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="date"
                          name="dob"
                          value={trainee.dob}
                          onChange={(e) => handleTraineeChange(index, e)}
                          max={new Date().toISOString().split("T")[0]}
                          required
                          className="border-2 border-slate-200 rounded-xl px-3 py-2.5 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Gender <span className="text-red-400">*</span>
                        </label>
                        <div className="flex gap-3 pt-2">
                          {['Male', 'Female'].map(g => (
                            <label
                              key={g}
                              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border-2 cursor-pointer text-sm font-bold transition-all ${
                                trainee.gender === g
                                  ? 'border-[#0b9949] bg-emerald-50 text-[#0b9949]'
                                  : 'border-slate-200 text-slate-500 hover:border-slate-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`gender-${index}`}
                                value={g}
                                checked={trainee.gender === g}
                                onChange={() => handleTraineeChange(index, { target: { name: 'gender', value: g } })}
                                className="hidden"
                              />
                              {g === 'Male' ? '♂' : '♀'} {g}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Row 3: Nationality + Race */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Nationality <span className="text-red-400">*</span>
                        </label>
                        <SearchableDropdown
                          name="nationality"
                          value={trainee.nationality}
                          onChange={(e) => handleTraineeChange(index, e)}
                          options={lookups?.nationalities || []}
                          placeholder="Select Nationality"
                          className="px-3 py-2.5"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Race <span className="text-red-400">*</span>
                        </label>
                        <SearchableDropdown
                          name="race"
                          value={trainee.race}
                          onChange={(e) => handleTraineeChange(index, e)}
                          options={lookups?.races || []}
                          placeholder="Select Race"
                          className="px-3 py-2.5"
                        />
                      </div>
                    </div>

                    {/* Row 4: Contact + Email */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Mobile Number *
                        </label>
                        <input
                          type="tel"
                          inputMode="tel"
                          name="traineeContact"
                          placeholder="e.g. +65 9123 4567"
                          value={trainee.traineeContact}
                          onChange={(e) => handleTraineeChange(index, e)}
                          onKeyPress={(e) => {
                            if (!/[0-9+\-()\s]/.test(e.key)) e.preventDefault();
                          }}
                          className="border-2 border-slate-200 rounded-xl px-3 py-2.5 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="traineeEmail"
                          placeholder="trainee@company.com"
                          value={trainee.traineeEmail}
                          onChange={(e) => handleTraineeChange(index, e)}
                          className="border-2 border-slate-200 rounded-xl px-3 py-2.5 w-full text-sm focus:border-[#0b9949] focus:outline-none transition-colors"
                          required
                        />
                      </div>
                    </div>

                    {/* Trainee Document Uploads */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                      {/* NRIC / Work Permit Upload */}
                      <div className="flex flex-col relative group mt-1">
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          NRIC / Work Permit Upload
                        </label>
                        <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-3 bg-slate-50/50 hover:bg-slate-50 hover:border-[#0b9949] transition-all duration-300 flex flex-col items-center justify-center text-center">
                          <input
                            key={trainee.workPermit ? (typeof trainee.workPermit === 'string' ? 'imported-wp' : trainee.workPermit.name) : 'empty-wp'}
                            type="file"
                            onChange={(e) => handleTraineeFileChange(index, 'workPermit', e.target.files[0])}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          {!trainee.workPermit ? (
                            <div>
                              <p className="text-xs font-semibold text-[#0b9949] hover:underline cursor-pointer">Choose File</p>
                              <p className="text-[9px] text-slate-500 mt-0.5">Note: File must be JPG, JPEG, PNG or PDF And Max Size 1 MB.</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-2.5 py-1 rounded-lg text-[10px] font-bold">
                                <span className="truncate max-w-[120px]">
                                  {typeof trainee.workPermit === 'string' ? 'Imported NRIC' : trainee.workPermit.name}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* WSQ Certificate Upload */}
                      <div className="flex flex-col relative group mt-1">
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          WSQ Certificate Upload
                        </label>
                        <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-3 bg-slate-50/50 hover:bg-slate-50 hover:border-[#0b9949] transition-all duration-300 flex flex-col items-center justify-center text-center">
                          <input
                            key={trainee.wsqCert ? (typeof trainee.wsqCert === 'string' ? 'imported-wsq' : trainee.wsqCert.name) : 'empty-wsq'}
                            type="file"
                            onChange={(e) => handleTraineeFileChange(index, 'wsqCert', e.target.files[0])}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.bmp"
                          />
                          {!trainee.wsqCert ? (
                            <div>
                              <p className="text-xs font-semibold text-[#0b9949] hover:underline cursor-pointer">Choose File</p>
                              <p className="text-[9px] text-slate-500 mt-0.5">Note: File must be JPG, GIF, PNG, PDF, Doc, Excel or BMP And Max Size 1 MB.</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-2.5 py-1 rounded-lg text-[10px] font-bold">
                                <span className="truncate max-w-[120px]">
                                  {typeof trainee.wsqCert === 'string' ? 'Imported WSQ Cert' : trainee.wsqCert.name}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Education Certificate Upload */}
                      <div className="flex flex-col relative group mt-1">
                        <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                          Education Certificate
                        </label>
                        <div className="relative border-2 border-dashed border-slate-200 rounded-xl p-3 bg-slate-50/50 hover:bg-slate-50 hover:border-[#0b9949] transition-all duration-300 flex flex-col items-center justify-center text-center">
                          <input
                            key={trainee.educationCert ? (typeof trainee.educationCert === 'string' ? 'imported-edu' : trainee.educationCert.name) : 'empty-edu'}
                            type="file"
                            onChange={(e) => handleTraineeFileChange(index, 'educationCert', e.target.files[0])}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          {!trainee.educationCert ? (
                            <div>
                              <p className="text-xs font-semibold text-[#0b9949] hover:underline cursor-pointer">Choose File</p>
                              <p className="text-[9px] text-slate-500 mt-0.5">Note: File must be JPG, JPEG, PNG or PDF And Max Size 1 MB.</p>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center">
                              <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-2.5 py-1 rounded-lg text-[10px] font-bold">
                                <span className="truncate max-w-[120px]">
                                  {typeof trainee.educationCert === 'string' ? 'Imported Edu Cert' : trainee.educationCert.name}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addTrainee}
              className="bg-[#0b9949] text-white px-4 py-2 rounded hover:bg-[#087c3a] transition-all shadow-md hover:scale-105 mt-4"
            >
              + Add Student Manually
            </button >
          </section >

          {/* Other Info */}
          < section >
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Name</label>
                <input
                  type="text"
                  readOnly
                  value={formData.courseName}
                  className="border rounded-md px-3 py-2 w-full bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount (with GST)</label>
                <input
                  type="text"
                  readOnly
                  value={`$${formData.courseAmount}`}
                  className="border rounded-md px-3 py-2 w-full bg-gray-100 text-gray-600 cursor-not-allowed font-semibold"
                />
              </div>
            </div>

            <select
              name="howKnow"
              value={formData.howKnow}
              onChange={handleChange}
              className="border rounded-md px-3 py-2 w-full mb-4 focus:ring-2 focus:ring-[#0b9949]"
            >
              <option value="">How got to know Greenmark?</option>
              <option value="Greenmark Staff">Greenmark Staff</option>
              <option value="Colleague">Colleague</option>
              <option value="Advertisement">Advertisement</option>
              <option value="Search Engine">Search Engine</option>
              <option value="Social Media">Social Media</option>
            </select>
          </section >

          {/* Staff Referral */}
          <section>
            <div className="mb-4">
              <label className="block font-medium mb-2 text-gray-800">Do you know our staff?</label>
              <div className="flex gap-6">
                <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                  <input
                    type="radio"
                    name="knowStaff"
                    value="Yes"
                    checked={formData.knowStaff === "Yes"}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                  <input
                    type="radio"
                    name="knowStaff"
                    value="No"
                    checked={formData.knowStaff === "No"}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300"
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            <AnimatePresence>
              {formData.knowStaff === "Yes" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid md:grid-cols-2 gap-6 mt-2 pt-2 pb-4">
                    {/* Staff Name Input */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Sales Staff Name *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="staffName"
                          placeholder="e.g. jack"
                          value={formData.staffName}
                          onChange={handleChange}
                          required={formData.knowStaff === "Yes"}
                          autoComplete="off"
                          className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide pr-12 transition-all duration-300 outline-none
                            ${staffLookupState === 'found' ? 'border-emerald-400 bg-emerald-50/30 focus:border-emerald-500' : 
                              staffLookupState === 'not_found' ? 'border-amber-300 bg-amber-50/20 focus:border-amber-400' : 
                              staffLookupState === 'error' ? 'border-red-300 bg-red-50/20' :
                              'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                        />
                        {/* Status Icon */}
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-base">
                          {staffLookupState === 'loading' && (
                            <svg className="animate-spin w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                          )}
                          {staffLookupState === 'found' && <span className="text-emerald-500 font-bold text-lg">✓</span>}
                          {staffLookupState === 'not_found' && <span className="text-amber-400 font-bold text-lg">?</span>}
                          {staffLookupState === 'error' && <span className="text-red-400 font-bold text-lg">!</span>}
                        </div>
                      </div>

                      {/* Live status message */}
                      <AnimatePresence mode="wait">
                        {staffLookupState === 'found' && (
                          <motion.div
                            key="found"
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3"
                          >
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                              <span className="text-white text-xs font-black">✓</span>
                            </div>
                            <div>
                              <p className="text-xs font-extrabold text-emerald-800 uppercase tracking-wide">Valid Sales Staff</p>
                              <p className="text-sm font-bold text-emerald-700 mt-0.5">{staffOfficialName}</p>
                              <p className="text-[10px] text-emerald-600 mt-0.5">Official sales email auto-filled ↓</p>
                            </div>
                          </motion.div>
                        )}
                        {staffLookupState === 'not_found' && (
                          <motion.div
                            key="not_found"
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2.5"
                          >
                            <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                              <span className="text-white text-xs font-extrabold">?</span>
                            </div>
                            <div>
                              <p className="text-xs font-extrabold text-amber-800 uppercase tracking-wide">Not Found</p>
                              <p className="text-sm font-semibold text-amber-700 mt-0.5">Sales staff name not found in system.</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Staff Email Input */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Sales Staff Email</label>
                      <input
                        type="email"
                        name="staffEmail"
                        placeholder="Sales Staff Email (e.g. name@greenmark.com.sg)"
                        value={formData.staffEmail}
                        onChange={handleChange}
                        readOnly={staffLookupState === 'found'}
                        className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide transition-all duration-300 outline-none
                          ${staffLookupState === 'found' 
                            ? 'border-emerald-300 bg-gray-50 text-gray-500 cursor-not-allowed' 
                            : 'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          {/* Agreement + Submit */}
          < div className="flex items-center gap-2" >
            <input
              type="checkbox"
              name="agree"
              checked={formData.agree}
              onChange={handleChange}
            />
            <label>
              By registering for this course, I accept{" "}
              
                Terms and Conditions
              
            </label>
          </div >

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSubmit}
            disabled={!formData.agree || isSubmitting}
            className={`w-full py-4 px-6 rounded-xl mt-8 font-bold text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${formData.agree && !isSubmitting
              ? "bg-[#0b9949] hover:bg-[#097c3c] text-white cursor-pointer hover:shadow-xl hover:-translate-y-1"
              : "bg-slate-200 text-slate-500 cursor-not-allowed opacity-80"
              }`}
          >
            {!formData.agree ? <Lock size={20} /> : (isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle2 size={20} />)}
            {isSubmitting ? "Processing Registration..." : (formData.agree ? "Confirm Registration" : "Accept Terms to Submit")}
          </motion.button>
        </div>
      </div >

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <SuccessModal onClose={handleModalClose} />
        )}
      </AnimatePresence>

    </>
  );
} 